/**
 * Canonical display label for every `UserPlace.placeRole` value, plus the one
 * accessor every surface should render through.
 *
 * The single source of PlaceRole copy — render via {@link getPlaceRoleLabel} and
 * never title-case the raw enum inline, or the same role reads two different ways
 * on two screens (`capitalize("SERVICE_COORDINATOR")` → "Service_coordinator").
 *
 * Lives in `libs/` rather than a feature slice because feature code, shared
 * household UI, and `components/ui/**` all need it — and `components/` must not
 * import upward from `features/`.
 *
 * Deprecated values keep their label so rows not yet migrated still render.
 */
export const ROLE_LABELS: Record<string, string> = {
  OWNER: "Owner",
  TENANT: "Tenant",
  ASSISTANT: "Assistant",
  GUEST: "Guest",
  SPONSOR: "Sponsor",
  SERVICE_COORDINATOR: "Service Coordinator",
  /** @deprecated Legacy place role; use SERVICE_COORDINATOR. */
  MANAGER: "Service Coordinator",
  /** @deprecated Legacy place role; use ASSISTANT. */
  PROPERTY_MANAGER: "Property Manager",
};

/**
 * Display label for a `UserPlace.placeRole`. Prefers the canonical
 * {@link ROLE_LABELS} copy (`SERVICE_COORDINATOR` → "Service Coordinator");
 * title-cases unknown values as a fallback.
 *
 * @param role - The raw `placeRole` value.
 * @returns The display label, or `null` for an empty value.
 */
export const getPlaceRoleLabel = (role: string) => {
  if (!role) {
    return null;
  }
  return (
    ROLE_LABELS[role] ??
    role
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
};
